import type Phaser from "phaser";
import layoutTable from "../../data/tables/ui/layout.json";
import themeTable from "../../data/tables/ui/theme.json";
import { applyTheme } from "./apply-theme";
import { clearHudShell, mountHudShell, type HudShellHandle } from "./hud-shell";
import { SafeFrame } from "./safe-frame";
import type { UiHudPreset, UiLayoutTable, UiThemeTable } from "./types";
import {
  attachViewportScale,
  type ViewportScaleHandle,
} from "./viewport-scale";

const layout = layoutTable as UiLayoutTable;
const theme = themeTable as unknown as UiThemeTable;

export type UiLayoutApi = {
  /** SafeFrame element — mount HUD shells / menus here. */
  frame: HTMLElement;
  /** Full-screen overlay host (`#screen-overlay`). */
  overlay: HTMLElement;
  sync: () => void;
  /** Re-apply viewport size + Phaser Scale.FIT (orientation / WebView). */
  refreshScale: () => void;
  mountShell: (preset: UiHudPreset) => HudShellHandle;
  clearShell: () => void;
  destroy: () => void;
  layout: UiLayoutTable;
};

let singleton: UiLayoutApi | null = null;

function overlayHost(): HTMLElement {
  const el = document.getElementById("screen-overlay");
  if (!el) throw new Error("#screen-overlay missing");
  return el;
}

/**
 * Attach shared UI layout engine (SafeFrame + theme). Call once after Phaser.Game create.
 */
export function createUiLayout(game: Phaser.Game): UiLayoutApi {
  if (singleton) {
    singleton.destroy();
    singleton = null;
  }

  const overlay = overlayHost();
  overlay.replaceChildren();
  overlay.style.pointerEvents = "none";
  // Host stays full viewport; children live in SafeFrame.
  overlay.style.display = "block";
  overlay.style.padding = "0";

  applyTheme(document.documentElement, theme);

  const safe = new SafeFrame(game, overlay);
  const frame = safe.element;
  frame.style.pointerEvents = "none";

  let viewport: ViewportScaleHandle | null = attachViewportScale(game, {
    onAfterRefresh: () => safe.sync(),
  });

  const api: UiLayoutApi = {
    frame,
    overlay,
    sync: () => safe.sync(),
    refreshScale: () => viewport?.refresh(),
    mountShell: (preset) => mountHudShell(frame, preset),
    clearShell: () => clearHudShell(frame),
    destroy: () => {
      viewport?.destroy();
      viewport = null;
      safe.destroy();
      clearHudShell(frame);
      if (singleton === api) singleton = null;
    },
    layout,
  };

  singleton = api;
  return api;
}

export function getUiLayout(): UiLayoutApi {
  if (!singleton) throw new Error("UI layout not initialized — call createUiLayout first");
  return singleton;
}

export function tryGetUiLayout(): UiLayoutApi | null {
  return singleton;
}
